import { useEffect, useState } from 'react';
import {
  getProAvailability,
  rescheduleBooking,
  type AvailabilitySlot,
} from '../../lib/booking-api';
import { customerApiErrorMessage } from '../../lib/customer-api';
import { toast } from '../../lib/toast';
import { ukSlotToISO } from '../../lib/uk-time';

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  professionalId: string;
  durationMinutes: number;
  onDone: () => void;
}

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/**
 * Customer-facing reschedule picker. Loads the SAME pro's live
 * availability (next 6 weeks) and lets the customer pick a new date +
 * time. On confirm the booking flips to "awaiting pro approval" — the
 * current time is kept until the pro approves. The submitted
 * scheduled_start is built the same way the booking wizard's
 * `computeScheduledStart` does (local wall-clock → ISO) so the time is
 * interpreted identically to the original booking.
 */
export function RescheduleModal({
  isOpen,
  onClose,
  bookingId,
  professionalId,
  durationMinutes,
  onDone,
}: RescheduleModalProps) {
  const [loading, setLoading] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState<
    Record<string, AvailabilitySlot[]>
  >({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setError(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const from = new Date();
        const to = new Date();
        to.setDate(to.getDate() + 42);
        const res = await getProAvailability({
          professional_id: professionalId,
          date_from: ymd(from),
          date_to: ymd(to),
          duration_minutes: durationMinutes,
        });
        if (!cancelled) setSlotsByDate(res?.data?.slots_by_date ?? {});
      } catch (err) {
        if (!cancelled)
          setError(
            customerApiErrorMessage(err, 'Could not load availability.'),
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, professionalId, durationMinutes]);

  if (!isOpen) return null;

  const dateKeys = Object.keys(slotsByDate)
    .filter((k) => (slotsByDate[k]?.length ?? 0) > 0)
    .sort();
  const timesForDate = selectedDate ? slotsByDate[selectedDate] ?? [] : [];

  const fmtDateChip = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime || submitting) return;
    // Interpret the picked slot as UK (Europe/London) wall-clock regardless of
    // the customer's browser timezone (see lib/uk-time). The old browser-local
    // setHours shifted the new time from a non-UK browser, so the backend's
    // reschedule slot-validation rejected it ("Could not request reschedule").
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const iso = ukSlotToISO(dt, selectedTime);
    setSubmitting(true);
    try {
      const res = await rescheduleBooking(bookingId, iso);
      toast.success(res?.message ?? 'Reschedule requested.');
      onDone();
      onClose();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not request reschedule.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[460px] max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 flex flex-col overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <div className="w-8" />
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] text-center flex-1">
              Reschedule booking
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f4f4f4] transition-colors"
            >
              <span className="material-icons text-[#3a3335] text-[20px]">
                close
              </span>
            </button>
          </div>
          <div className="h-[1px] bg-[#ede8e9] w-full mb-4" />
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] leading-[20px] mb-4 text-center">
            Pick a new time from your pro's availability. They'll be asked to
            approve it — your current time stays until they do.
          </p>

          {loading ? (
            <p className="text-center text-[#908e8e] text-sm py-8">
              Loading availability…
            </p>
          ) : error ? (
            <p className="text-center text-[#8e2a2f] text-sm py-8">{error}</p>
          ) : dateKeys.length === 0 ? (
            <p className="text-center text-[#908e8e] text-sm py-8">
              No open slots in the next 6 weeks. Please contact us to move this
              booking.
            </p>
          ) : (
            <div className="overflow-y-auto flex-1 -mx-1 px-1">
              <p className="font-['DM_Sans',sans-serif] font-semibold text-[#3a3335] text-[13px] mb-2">
                Date
              </p>
              <div className="flex gap-2 flex-wrap mb-4">
                {dateKeys.map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedDate(key);
                      setSelectedTime(null);
                    }}
                    className={`px-3 h-[36px] rounded-[10px] text-[13px] font-['DM_Sans',sans-serif] font-medium border transition-colors ${
                      selectedDate === key
                        ? 'bg-[#8e2a2f] text-white border-[#8e2a2f]'
                        : 'bg-white text-[#3a3335] border-[#ede8e9] hover:border-[#8e2a2f]'
                    }`}
                  >
                    {fmtDateChip(key)}
                  </button>
                ))}
              </div>
              {selectedDate && (
                <>
                  <p className="font-['DM_Sans',sans-serif] font-semibold text-[#3a3335] text-[13px] mb-2">
                    Time
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {timesForDate.map((slot) => (
                      <button
                        key={slot.start}
                        onClick={() => setSelectedTime(slot.start)}
                        className={`h-[36px] rounded-[10px] text-[13px] font-['DM_Sans',sans-serif] font-medium border transition-colors ${
                          selectedTime === slot.start
                            ? 'bg-[#8e2a2f] text-white border-[#8e2a2f]'
                            : 'bg-white text-[#3a3335] border-[#ede8e9] hover:border-[#8e2a2f]'
                        }`}
                      >
                        {slot.start}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="h-[1px] bg-[#ede8e9] w-full my-4" />
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-[44px] bg-[#fcfaf7] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-sm hover:bg-[#f2eee2] transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedTime || submitting}
              className="flex-1 h-[44px] bg-[#8e2a2f] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-white text-sm hover:bg-[#7a2329] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Requesting…' : 'Request reschedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
